import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Stack,
  Divider,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useNotify, useTranslate, Title } from 'react-admin';
import { siteSettingsService } from '../services/siteSettingsService';

const SiteSettingsPage = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [appLandingEnabled, setAppLandingEnabled] = useState(false);
  const [utilityBarEnabled, setUtilityBarEnabled] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await siteSettingsService.getSettings();
        if (!cancelled) {
          setAppLandingEnabled(data?.appLandingEnabled === true);
          setUtilityBarEnabled(data?.utilityBarEnabled !== false);
        }
      } catch (e) {
        if (!cancelled) {
          notify(translate('siteSettings.loadError'), { type: 'warning' });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [notify, translate]);

  const onSave = async () => {
    setSaving(true);
    try {
      await siteSettingsService.updateSettings({ appLandingEnabled, utilityBarEnabled });
      notify(translate('siteSettings.saved'), { type: 'info' });
    } catch (e) {
      notify(translate('siteSettings.saveError'), { type: 'warning' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 720 }}>
      <Title title={translate('siteSettings.title')} />
      <Typography variant="h5" sx={{ mb: 2 }}>
        {translate('siteSettings.title')}
      </Typography>

      <Alert severity="info" sx={{ mb: 2 }}>
        {translate('siteSettings.hint')}
      </Alert>

      {appLandingEnabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {translate('siteSettings.warning')}
        </Alert>
      )}

      <Card>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={appLandingEnabled}
                onChange={(e) => setAppLandingEnabled(e.target.checked)}
                color="primary"
              />
            }
            label={translate('siteSettings.appLanding')}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 6 }}>
            {translate('siteSettings.appLandingHelp')}
          </Typography>

          <Divider sx={{ my: 2 }} />

          <FormControlLabel
            control={
              <Switch
                checked={utilityBarEnabled}
                onChange={(e) => setUtilityBarEnabled(e.target.checked)}
                color="primary"
              />
            }
            label={translate('siteSettings.utilityBar')}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 6 }}>
            {translate('siteSettings.utilityBarHelp')}
          </Typography>

          <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />}
              onClick={onSave}
              disabled={saving}
            >
              {translate('ra.action.save')}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SiteSettingsPage;
